import React, { useState, useEffect, useRef } from 'react';
import { projectsAPI } from '../services/api';
import api from '../services/api';
import './ProjectFileUpload.css';

const ProjectFileUpload = ({ projectId, onUpload }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  // Thumbnails are fetched with the auth token (a plain <img src> can't send it) and shown as blob: URLs.
  const [thumbs, setThumbs] = useState({});
  const thumbsRef = useRef({});

  const handleImagePreview = (file) => {
    if (file.file_type !== 'image') {
      handleDownload(file);
      return;
    }
    setLightboxLoading(true);
    setLightboxImage(null);
    api.get(`/projects/files/${file.id}`, { responseType: 'blob' })
      .then((res) => {
        setLightboxImage(window.URL.createObjectURL(res.data));
      })
      .catch(() => {
        alert('Could not load this image');
      })
      .finally(() => setLightboxLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    files
      .filter((f) => f.file_type === 'image' && !(f.id in thumbsRef.current))
      .forEach((f) => {
        thumbsRef.current[f.id] = null; // loading
        projectsAPI.downloadFile(f.id)
          .then((res) => {
            const url = window.URL.createObjectURL(res.data);
            if (cancelled) {
              window.URL.revokeObjectURL(url);
              return;
            }
            thumbsRef.current[f.id] = url;
            setThumbs((prev) => ({ ...prev, [f.id]: url }));
          })
          .catch(() => {
            thumbsRef.current[f.id] = false; // failed: show the icon
            setThumbs((prev) => ({ ...prev, [f.id]: false }));
          });
      });
    return () => {
      cancelled = true;
    };
  }, [files]);

  useEffect(() => () => {
    Object.values(thumbsRef.current).forEach((url) => url && window.URL.revokeObjectURL(url));
  }, []);

  const closeLightbox = () => {
    setLightboxImage((prev) => {
      if (prev && prev.startsWith('blob:')) {
        window.URL.revokeObjectURL(prev);
      }
      return null;
    });
  };

  useEffect(() => {
    if (!lightboxImage) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [lightboxImage]);

  useEffect(() => {
    if (projectId) {
      loadFiles();
    }
  }, [projectId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getFiles(projectId);
      setFiles(response.data);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        alert(`File size exceeds 10MB limit. Selected file: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        e.target.value = '';
        return;
      }

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
          setPreviewFile(file);
        };
        reader.readAsDataURL(file);
      } else {
        setPreview(null);
        setPreviewFile(file);
      }
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      await projectsAPI.uploadFile(projectId, file);
      setPreview(null);
      setPreviewFile(null);
      if (onUpload) {
        onUpload();
      }
      loadFiles();
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await projectsAPI.deleteFile(fileId);
      loadFiles();
      if (onUpload) {
        onUpload();
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await projectsAPI.downloadFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  const getFileIcon = (fileType, fileName) => {
    if (fileType === 'image') return '🖼️';
    if (fileType === 'pdf' || fileName.endsWith('.pdf')) return '📄';
    if (fileType === 'document' || fileName.match(/\.(doc|docx|txt|rtf)$/i)) return '📝';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  if (loading) {
    return <div className="file-upload-loading">Loading files...</div>;
  }

  return (
    <div className="project-file-upload">
      <div className="file-upload-section">
        <label className="file-upload-label">
          {uploading ? 'Uploading...' : '📎 Upload File'}
          <input
            type="file"
            accept="*/*"
            onChange={(e) => {
              handleFileChange(e);
              handleUpload(e);
            }}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>
        {preview && (
          <div className="file-preview">
            <img src={preview} alt="Preview" />
          </div>
        )}
        {previewFile && !preview && (
          <div className="file-preview-text">
            Selected: {previewFile.name} ({(previewFile.size / (1024 * 1024)).toFixed(2)}MB)
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div className="files-list">
          <h4>Uploaded Files ({files.length})</h4>
          <div className="files-grid">
            {files.map((file) => (
              <div key={file.id} className="file-item">
                {file.file_type === 'image' ? (
                  <div
                    className="file-preview-image"
                    onClick={() => handleImagePreview(file)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleImagePreview(file)}
                    aria-label={`Preview ${file.file_name}`}
                  >
                    {thumbs[file.id] ? (
                      <img src={thumbs[file.id]} alt={file.file_name} />
                    ) : (
                      <div className="file-icon-large">{getFileIcon(file.file_type, file.file_name)}</div>
                    )}
                  </div>
                ) : (
                  <div className="file-icon-large">{getFileIcon(file.file_type, file.file_name)}</div>
                )}
                <div className="file-info">
                  <div className="file-name" title={file.file_name}>
                    {file.file_name}
                  </div>
                  <div className="file-meta">
                    <span className="file-type">{file.file_type || 'other'}</span>
                    <span className="file-size">{formatFileSize(file.file_size)}</span>
                  </div>
                  <div className="file-date">
                    {new Date(file.uploaded_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="file-actions">
                  <button
                    className="btn-download"
                    onClick={() => handleDownload(file)}
                    title="Download"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                  <button
                    className="btn-delete-small"
                    onClick={() => handleDelete(file.id)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {files.length === 0 && !loading && (
        <div className="files-empty">
          <p>No files uploaded yet.</p>
          <p>Click "Upload File" to add pictures, documents, or other files to this project.</p>
        </div>
      )}

      {(lightboxImage || lightboxLoading) && (
        <div
          className="file-preview-modal"
          onClick={closeLightbox}
          role="button"
          tabIndex={0}
          aria-label="Close preview"
        >
          <button
            className="file-preview-modal-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ×
          </button>
          {lightboxLoading ? (
            <div className="file-preview-modal-loading">Loading...</div>
          ) : (
            <img
              className="file-preview-modal-content"
              src={lightboxImage}
              alt="Preview"
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectFileUpload;
